/**
 * Data hooks for open orders, order history and fills (#671, #672).
 *
 *   useOpenOrders(address)   the open orders the user session holds (#1809, userSession.ts).
 *   useOrderHistory(address) the order history the user session holds (#1809, sessionHistory.ts).
 *   useFills(address)        the fills the user session holds (#1809, sessionFills.ts).
 *   useOrderUpdates(address) WS orderUpdates → orderEvents (app-wide emitter) + optional callback.
 *
 * Every hook resets when the address or the environment changes.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useHeld, useHeldFills, useHeldHistory } from '../UserSession';
import { useSubscription } from '../../hl/react';
import type { FrontendOrder, OrderUpdate, UserFill } from '../../hl/types';
import {
  findPositionMismatches,
  positionAfter,
  type PositionMismatch,
} from './model';
import { orderEvents } from './orderEvents';

function norm(address: string | null | undefined): string | null {
  return address ? address.toLowerCase() : null;
}

// ---------------------------------------------------------------------------------------------
// orderUpdates
// ---------------------------------------------------------------------------------------------

/** Subscribe to orderUpdates for `address`, feed the app-wide `orderEvents` emitter and call `onUpdates`. */
export function useOrderUpdates(address: string | null | undefined, onUpdates?: (u: OrderUpdate[]) => void): void {
  const user = norm(address);
  useSubscription(user ? { type: 'orderUpdates', user } : null, (updates) => {
    if (!user || !Array.isArray(updates)) return;
    orderEvents.emit(user, updates);
    onUpdates?.(updates);
  });
}

// ---------------------------------------------------------------------------------------------
// Open orders
// ---------------------------------------------------------------------------------------------

export interface OpenOrdersState {
  orders: FrontendOrder[];
  /** True until the first list. */
  loading: boolean;
  error: string | null;
  /** Where the current list came from last. */
  source: 'rest' | 'ws' | 'event' | null;
}

const NO_ORDERS: FrontendOrder[] = [];

/** The session's open orders (userSession.ts). Reads nothing itself. */
export function useOpenOrders(address: string | null | undefined): OpenOrdersState {
  const held = useHeld('orders', address);
  return {
    orders: held.data ?? NO_ORDERS,
    loading: held.isLoading,
    error: held.error ? held.error.message : null,
    source: held.source,
  };
}

// ---------------------------------------------------------------------------------------------
// Order history
// ---------------------------------------------------------------------------------------------

/** The session's order history (sessionHistory.ts). Reads nothing itself; paging it is the panel's business. */
export const useOrderHistory = useHeldHistory;

// ---------------------------------------------------------------------------------------------
// Fills
// ---------------------------------------------------------------------------------------------

export interface FillRow extends UserFill {
  /** startPosition + signed size. */
  positionAfter: string;
}

export interface FillsState {
  fills: FillRow[];
  loading: boolean;
  /** The session's download has answered. */
  complete: boolean;
  /** Older fills exist that are not held. */
  capped: boolean;
  error: string | null;
  /** Dev diagnostic: computed position-after that disagrees with the next fill's startPosition. */
  mismatches: PositionMismatch[];
}

/** The session's fills (sessionFills.ts). Reads nothing itself; paging them is the panel's business. */
export function useFills(address: string | null | undefined): FillsState {
  const st = useHeldFills(address);
  const rows = useMemo<FillRow[]>(
    () => st.fills.map((f) => ({ ...f, positionAfter: positionAfter(f) })),
    [st.fills],
  );
  const mismatches = useMemo(() => findPositionMismatches(st.fills), [st.fills]);

  // Dev assertion: log each new mismatch once.
  const logged = useRef(new Set<string>());
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    for (const m of mismatches) {
      const k = `${m.tid}:${m.nextTid}`;
      if (logged.current.has(k)) continue;
      logged.current.add(k);
      console.warn(
        `[fills] position mismatch on ${m.coin}: tid ${m.tid} → ${m.computed}, next tid ${m.nextTid} startPosition ${m.nextStart}`,
      );
    }
  }, [mismatches]);

  return { ...st, fills: rows, mismatches };
}
